import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import { registerSW } from "virtual:pwa-register";
import App from "./App.tsx";
import "./index.css";
import "leaflet/dist/leaflet.css";
import "./i18n/index";

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // supabase-js auth-token Web Lock stealing (internal background refresh
    // races, e.g. mobile tab resume) rejects where app code can't catch it.
    ignoreErrors: [/^Lock "lock:sb-.+-auth-token" was released because another request stole it$/],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
  });
}

const REQUIRED_ENV = ["VITE_SUPABASE_URL", "VITE_SUPABASE_ANON_KEY"] as const;
for (const key of REQUIRED_ENV) {
  if (!import.meta.env[key]) {
    document.body.innerHTML = `<p style="font-family:sans-serif;padding:2rem;color:#b91c1c">Configuration error: <code>${key}</code> is not set. Please contact the administrator.</p>`;
    throw new Error(`Missing required environment variable: ${key}`);
  }
}

registerSW({
  onNeedRefresh() {
    if (confirm("A new version of VGV is available. Reload now to update?")) {
      window.location.reload();
    }
  },
});

function ErrorFallback() {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem", fontFamily: "system-ui, sans-serif", textAlign: "center" }}>
      <div style={{ maxWidth: 480 }}>
        <h1 style={{ fontSize: "1.5rem", marginBottom: "0.75rem", color: "#1a1a1a" }}>Something went wrong</h1>
        <p style={{ color: "#666", marginBottom: "1.5rem", lineHeight: 1.5 }}>
          We've been notified and are looking into it. Please reload the page to continue.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{ padding: "0.625rem 1.25rem", background: "#1a56db", color: "white", border: "none", borderRadius: 6, fontSize: "0.95rem", cursor: "pointer" }}
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
    <App />
  </Sentry.ErrorBoundary>
);
